// hooks/useAuth.jsx
import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
  useRef
} from "react";
import supabaseService from "../services/supabaseService";
import offlineStorage from "../services/offlineStorage"; // 🆕

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  
  const isMountedRef = useRef(true);
  const retryCountRef = useRef(0);
  const initializingRef = useRef(false);
  const maxRetries = 2;
  const initTimeoutRef = useRef(null);

  const isBrowser = typeof window !== 'undefined';

  // ─────────────────────────────────────────────────
  //  حفظ / استرجاع نوع المستخدم محلياً
  // ─────────────────────────────────────────────────
  const saveUserTypeLocally = useCallback((userId, type) => {
    if (!isBrowser) return;
    try {
      const userData = { userId, type, timestamp: Date.now() };
      localStorage.setItem(`user_type_${userId}`, JSON.stringify(userData));
      console.log('💾 Saved user type locally:', type);
    } catch (e) {
      console.error('Error saving user type:', e);
    }
  }, [isBrowser]);

  const getUserTypeFromStorage = useCallback((userId) => {
    if (!isBrowser) return null;
    try {
      const data = localStorage.getItem(`user_type_${userId}`);
      if (data) {
        const parsed = JSON.parse(data);
        if (Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
          console.log('✅ Retrieved user type from storage:', parsed.type);
          return parsed.type;
        } else {
          localStorage.removeItem(`user_type_${userId}`);
        }
      }
    } catch (e) {
      console.error('Error retrieving user type:', e);
    }
    return null;
  }, [isBrowser]);

  const cleanupInvalidSession = useCallback(async () => {
    if (!isBrowser) return;
    try {
      const authToken = localStorage.getItem('supabase.auth.token');
      if (authToken) {
        try {
          const parsed = JSON.parse(authToken);
          if (parsed.expires_at && parsed.expires_at < Date.now() / 1000) {
            console.log('🧹 Cleaning expired session...');
            localStorage.removeItem('supabase.auth.token');
            sessionStorage.clear();
            await supabaseService.client.auth.signOut();
            return true;
          }
        } catch (e) {
          console.log('🧹 Cleaning malformed session...');
          localStorage.removeItem('supabase.auth.token');
          sessionStorage.clear();
          return true;
        }
      }
    } catch (error) {
      console.error('Cleanup error:', error);
    }
    return false;
  }, [isBrowser]);

  // ─────────────────────────────────────────────────
  //  🆕 استرجاع الجلسة من offlineStorage عند عدم الاتصال
  // ─────────────────────────────────────────────────
  const tryOfflineSession = useCallback(async () => {
    try {
      const saved = await offlineStorage.loadSession();
      if (!saved?.session?.user) return false;

      console.log('📴 [Offline] Restoring session from encrypted cache...');
      const { user: offlineUser, userId } = saved;

      // نستخدم user من داخل session
      const u = saved.session.user;
      if (!u) return false;

      if (isMountedRef.current) {
        setUser(u);
        // استرجاع نوع المستخدم من localStorage (لا يحتاج شبكة)
        const cachedType = getUserTypeFromStorage(u.id);
        setUserType(cachedType || null);
        console.log('✅ [Offline] Session restored. userType:', cachedType);
      }
      return true;
    } catch (err) {
      console.error('❌ [Offline] Failed to restore session:', err);
      return false;
    }
  }, [getUserTypeFromStorage]);

  // ─────────────────────────────────────────────────
  //  checkUserType
  // ─────────────────────────────────────────────────
  const checkUserType = useCallback(async (userId, retryCount = 0) => {
    if (!userId || !isBrowser) return null;

    try {
      console.log('🔍 Checking user type for:', userId.substring(0, 8) + '...', `(Attempt ${retryCount + 1})`);
      
      const cachedType = getUserTypeFromStorage(userId);
      if (cachedType) {
        console.log('⚡ Using cached user type:', cachedType);
        return cachedType;
      }

      // إذا بدون إنترنت لا نحاول الشبكة
      if (!navigator.onLine) {
        console.warn('📴 Offline — cannot fetch user type from network');
        return null;
      }
      
      if (retryCount > 0) {
        await new Promise(resolve => setTimeout(resolve, 400 * retryCount));
      }
      
      const checkWithTimeout = async () => {
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Query timeout')), 4000)
        );
        const queryPromise = Promise.allSettled([
          supabaseService.getProviderById(userId),
          supabaseService.getClientById(userId)
        ]);
        return Promise.race([queryPromise, timeoutPromise]);
      };
      
      const [providerResult, clientResult] = await checkWithTimeout();

      if (providerResult.status === 'fulfilled' && providerResult.value) {
        console.log('✅ User is a provider');
        saveUserTypeLocally(userId, 'provider');
        return "provider";
      }
      if (clientResult.status === 'fulfilled' && clientResult.value) {
        console.log('✅ User is a client');
        saveUserTypeLocally(userId, 'client');
        return "client";
      }

      if (retryCount < 2) {
        console.log(`🔄 User type not found, retrying... (${retryCount + 1}/2)`);
        return await checkUserType(userId, retryCount + 1);
      }

      console.log('❓ User type not found after all retries');
      return null;
    } catch (error) {
      console.error('❌ Error checking user type:', error);
      if (retryCount < 2 && (error.message?.includes('fetch') || error.message?.includes('network'))) {
        return await checkUserType(userId, retryCount + 1);
      }
      return null;
    }
  }, [isBrowser, getUserTypeFromStorage, saveUserTypeLocally]);

  // ─────────────────────────────────────────────────
  //  updateUserState
  // ─────────────────────────────────────────────────
  const updateUserState = useCallback(async (session) => {
    if (!isMountedRef.current) return;
    
    try {
      setError(null);
      
      if (!session || !session.access_token) {
        const wasCleaned = await cleanupInvalidSession();
        if (wasCleaned) console.log('🧹 Session cleaned, setting user to null');

        // 🆕 محاولة offline قبل الاستسلام
        if (!navigator.onLine) {
          const restoredOffline = await tryOfflineSession();
          if (restoredOffline) return;
        }

        setUser(null);
        setUserType(null);
        return;
      }

      if (session.expires_at && session.expires_at < Date.now() / 1000) {
        console.log('🕒 Session expired, cleaning up...');
        await cleanupInvalidSession();

        // 🆕 محاولة offline
        if (!navigator.onLine) {
          const restoredOffline = await tryOfflineSession();
          if (restoredOffline) return;
        }

        setUser(null);
        setUserType(null);
        return;
      }

      console.log('👤 Setting user from valid session');
      setUser(session.user);

      // 🆕 حفظ الجلسة في offlineStorage كلما كانت صالحة
      if (session.user?.id) {
        offlineStorage.saveSession(session.user.id, {
          user:          session.user,
          access_token:  session.access_token,
          refresh_token: session.refresh_token,
          expires_at:    session.expires_at,
        }).catch(() => {});
      }
      
      const cachedType = getUserTypeFromStorage(session.user.id);
      if (cachedType) {
        setUserType(cachedType);
        console.log('⚡ Using cached user type:', cachedType);
        return;
      }
      
      const type = await checkUserType(session.user.id);
      if (type) {
        setUserType(type);
        console.log('✅ User type set successfully:', type);
      } else {
        console.warn('⚠️ Could not determine user type');
        setUserType(null);
      }
      
    } catch (error) {
      console.error('❌ Error in updateUserState:', error);
      setUserType(null);
    }
  }, [checkUserType, cleanupInvalidSession, getUserTypeFromStorage, tryOfflineSession]);

  // ─────────────────────────────────────────────────
  //  handleEmailConfirmation
  // ─────────────────────────────────────────────────
  const handleEmailConfirmation = useCallback(async (accessToken, refreshToken) => {
    if (!isBrowser) return false;
    try {
      console.log('🔐 Processing email confirmation with token...');
      const { data, error } = await supabaseService.client.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
      if (error) { console.error('❌ Error setting session:', error); return false; }
      if (data?.session?.user) {
        console.log('✅ Session set successfully from email confirmation');
        if (isMountedRef.current) { setUser(data.session.user); setUserType(null); }
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Error in handleEmailConfirmation:', error);
      return false;
    }
  }, [isBrowser]);

  // ─────────────────────────────────────────────────
  //  initializeAuth — المكان الرئيسي للإصلاح
  // ─────────────────────────────────────────────────
  const initializeAuth = useCallback(async () => {
    if (!isBrowser || initializingRef.current) return;
    initializingRef.current = true;

    initTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current && loading) {
        console.warn('⚠️ Initialization timeout - forcing completion');
        setLoading(false);
        setIsInitialized(true);
        initializingRef.current = false;
      }
    }, 5000);

    try {
      console.log('🚀 Initializing authentication...');
      
      const isGuestMode = localStorage.getItem('is_guest') === 'true';
      if (isGuestMode) {
        console.log('👀 Guest mode detected on initialization');
        if (isMountedRef.current) {
          setUser({ id: 'guest', email: 'guest@khidma.com', isGuest: true });
          setUserType('client');
        }
        if (initTimeoutRef.current) clearTimeout(initTimeoutRef.current);
        if (isMountedRef.current) {
          setLoading(false);
          setIsInitialized(true);
          initializingRef.current = false;
        }
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 300));
      await supabaseService.waitForInit();
      
      // تحقق من وضع استعادة كلمة المرور
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const type = hashParams.get('type');
      const accessToken = hashParams.get('access_token');
      
      if (type === 'recovery' && accessToken) {
        console.log('🔑 Password recovery mode detected');
        setIsRecoveryMode(true);
        setLoading(false);
        setIsInitialized(true);
        initializingRef.current = false;
        clearTimeout(initTimeoutRef.current);
        return;
      }

      // ─── 🆕 إذا بدون إنترنت — اذهب مباشرة للكاش ───
      if (!navigator.onLine) {
        console.log('📴 No internet — trying offline session cache...');
        const restoredOffline = await tryOfflineSession();
        if (restoredOffline) {
          console.log('✅ Offline session restored successfully');
        } else {
          console.warn('⚠️ No offline session found');
          setUser(null);
          setUserType(null);
        }
        return; // تخطي كل استعلامات Supabase
      }

      // ─── وضع عادي مع إنترنت ───
      await cleanupInvalidSession();
      
      const { data: { session }, error: sessionError } = await supabaseService.client.auth.getSession();
      
      if (sessionError) {
        console.error('❌ Session error:', sessionError);

        if (sessionError.message?.includes('session') || sessionError.message?.includes('token')) {
          localStorage.removeItem('supabase.auth.token');
          sessionStorage.clear();
          await supabaseService.client.auth.signOut();
        }

        // 🆕 محاولة offline عند خطأ الجلسة
        const restoredOffline = await tryOfflineSession();
        if (!restoredOffline && isMountedRef.current) {
          setUser(null);
          setUserType(null);
        }
      } else {
        // حفظ فوري للجلسة بمجرد نجاح getSession
        if (session?.user?.id) {
          offlineStorage.saveSession(session.user.id, {
            user:          session.user,
            access_token:  session.access_token,
            refresh_token: session.refresh_token,
            expires_at:    session.expires_at,
          }).catch(() => {});
          console.log('Session saved to offline cache immediately on load');
        }
        if (isMountedRef.current) {
          await updateUserState(session);
        }
      }
      
    } catch (error) {
      console.error('❌ Initialization error:', error);

      // 🆕 محاولة offline عند أي استثناء (خطأ شبكة، timeout...)
      const restoredOffline = await tryOfflineSession();
      if (!restoredOffline && isMountedRef.current) {
        console.warn('⚠️ Continuing with limited functionality');
        setUser(null);
        setUserType(null);
      }
    } finally {
      if (initTimeoutRef.current) clearTimeout(initTimeoutRef.current);
      if (isMountedRef.current) {
        setLoading(false);
        setIsInitialized(true);
        initializingRef.current = false;
      }
    }
  }, [isBrowser, loading, cleanupInvalidSession, updateUserState, tryOfflineSession]);

  // ─────────────────────────────────────────────────
  //  useEffect — التهيئة الأولى
  // ─────────────────────────────────────────────────
  useEffect(() => {
    isMountedRef.current = true;
    
    const handleUrlHash = async () => {
      if (!isBrowser) return;
      try {
        console.log('🔍 Checking URL parameters...');
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const type = hashParams.get('type');
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        console.log('📍 URL parameters:', { type, hasAccessToken: !!accessToken, hasRefreshToken: !!refreshToken });

        if (type === 'email' && accessToken && refreshToken) {
          console.log('📧 Email confirmation detected');
          const ok = await handleEmailConfirmation(accessToken, refreshToken);
          if (ok && isMountedRef.current) {
            window.history.replaceState(null, '', window.location.pathname);
            return;
          }
        }
        if (type === 'recovery' && accessToken) {
          console.log('🔑 Password recovery detected');
          setIsRecoveryMode(true);
          window.history.replaceState(null, '', window.location.pathname);
          return;
        }
      } catch (error) {
        console.error('❌ Error handling URL hash:', error);
      }
    };

    const init = async () => {
      await handleUrlHash();
      if (isRecoveryMode) { setLoading(false); setIsInitialized(true); return; }
      await initializeAuth();
    };

    if (!isInitialized) init();

    return () => {
      isMountedRef.current = false;
      if (initTimeoutRef.current) clearTimeout(initTimeoutRef.current);
    };
  }, [isBrowser, isInitialized, initializeAuth, isRecoveryMode, handleEmailConfirmation]);

  // ─────────────────────────────────────────────────
  //  useEffect — auth state listener
  // ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isBrowser || isRecoveryMode) return;

    const { data: authListener } = supabaseService.client.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMountedRef.current) return;
        console.log(`🎯 Auth state change: ${event}`);
        
        switch (event) {
          case 'SIGNED_IN':
            await updateUserState(session);
            // 🆕 حفظ الجلسة offline عند أي تسجيل دخول (شامل Google)
            if (session?.user?.id) {
              offlineStorage.saveSession(session.user.id, {
                user:          session.user,
                access_token:  session.access_token,
                refresh_token: session.refresh_token,
                expires_at:    session.expires_at,
              }).catch(() => {});
              if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({ type: 'USER_LOGGED_IN' });
              }
            }
            break;
            
          case 'SIGNED_OUT':
            // 🆕 مسح بيانات offline
            if (user?.id) {
              offlineStorage.clearAllUserData(user.id).catch(() => {});
              localStorage.removeItem(`user_type_${user.id}`);
            }
            if (isBrowser) {
              localStorage.removeItem('supabase.auth.token');
              sessionStorage.clear();
            }
            if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
              navigator.serviceWorker.controller.postMessage({ type: 'USER_LOGGED_OUT' });
            }
            setUser(null);
            setUserType(null);
            setError(null);
            retryCountRef.current = 0;
            break;
            
          case 'USER_UPDATED':
            if (session?.user) setUser(session.user);
            break;
            
          case 'TOKEN_REFRESHED':
            console.log('🔄 Token refreshed successfully');
            // 🆕 تحديث الجلسة المحفوظة offline
            if (session?.user?.id) {
              offlineStorage.saveSession(session.user.id, {
                user:          session.user,
                access_token:  session.access_token,
                refresh_token: session.refresh_token,
                expires_at:    session.expires_at,
              }).catch(() => {});
            }
            break;
            
          case 'PASSWORD_RECOVERY':
            console.log('🔑 Password recovery in progress');
            setIsRecoveryMode(true);
            setUser(null);
            setUserType(null);
            break;
        }
      }
    );

    return () => {
      if (authListener?.subscription) authListener.subscription.unsubscribe();
    };
  }, [isBrowser, updateUserState, isRecoveryMode, user]);

  // ─────────────────────────────────────────────────
  //  reloadAuth
  // ─────────────────────────────────────────────────
  const reloadAuth = useCallback(async () => {
    if (isRecoveryMode) return;
    setLoading(true);
    try {
      // 🆕 إذا بدون إنترنت — من الكاش مباشرة
      if (!navigator.onLine) {
        await tryOfflineSession();
        return;
      }
      const { data: { session } } = await supabaseService.client.auth.getSession();
      await updateUserState(session);
    } catch (error) {
      console.error('Reload error:', error);
      // 🆕 fallback للكاش
      const restoredOffline = await tryOfflineSession();
      if (!restoredOffline) setError("فشل إعادة تحميل المصادقة");
    } finally {
      setLoading(false);
    }
  }, [updateUserState, isRecoveryMode, tryOfflineSession]);

  // ─────────────────────────────────────────────────
  //  🆕 مستمع لحالة الشبكة — يعيد التهيئة عند عودة الاتصال
  // ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isBrowser) return;
    const onOnline = async () => {
      // 🆕 تحقق حقيقي — navigator.onLine غير موثوق مع Service Worker
      try {
        const controller = new AbortController();
        setTimeout(() => controller.abort(), 3000);
        await fetch('https://reescgwvnnbvwgtadvdv.supabase.co/rest/v1/', {
          method: 'HEAD',
          cache: 'no-store',
          signal: controller.signal,
        });
      } catch {
        // لا يزال بدون إنترنت حقيقي — تجاهل الحدث
        console.log('📴 online event fired but no real connectivity — ignoring');
        return;
      }

      console.log('🌐 Connection truly restored — refreshing session...');
      if (!user) {
        try {
          const { data: { session } } = await supabaseService.client.auth.getSession();
          if (session) await updateUserState(session);
        } catch (e) {
          console.error('Failed to refresh after reconnect:', e);
        }
      }
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [isBrowser, user, updateUserState]);

  // ─────────────────────────────────────────────────
  //  Context Value
  // ─────────────────────────────────────────────────
  const value = {
    user,
    userType,
    loading: loading || !isInitialized,
    error,
    isInitialized,
    isRecoveryMode,
    
    signIn: async (email, password) => {
      if (isRecoveryMode) throw new Error("Cannot sign in during password recovery");
      setLoading(true);
      setError(null);
      retryCountRef.current = 0;
      try {
        const result = await supabaseService.signIn(email, password);
        if (result.error) throw result.error;
        // 🆕 حفظ الجلسة offline فوراً
        if (result.data?.session) {
          await offlineStorage.saveSession(result.data.user.id, {
            user:          result.data.user,
            access_token:  result.data.session.access_token,
            refresh_token: result.data.session.refresh_token,
            expires_at:    result.data.session.expires_at,
          });
        }
        await reloadAuth();
        return result;
      } catch (error) {
        setError(error.message || "فشل تسجيل الدخول");
        setLoading(false);
        throw error;
      }
    },

    signUp: async (email, password, metadata = {}) => {
      if (isRecoveryMode) throw new Error("Cannot sign up during password recovery");
      setLoading(true);
      setError(null);
      try {
        const result = await supabaseService.signUp(email, password, metadata);
        if (result.error) throw result.error;
        setLoading(false);
        return result;
      } catch (error) {
        setError(error.message || "فشل إنشاء الحساب");
        setLoading(false);
        throw error;
      }
    },

    signInWithGoogle: async () => {
      if (isRecoveryMode) throw new Error("Cannot sign in during password recovery");
      setLoading(true);
      setError(null);
      try {
        const result = await supabaseService.signInWithGoogle();
        if (result.error) throw result.error;
        return result;
      } catch (error) {
        setError(error.message || "فشل تسجيل الدخول بجوجل");
        setLoading(false);
        throw error;
      }
    },

    signOut: async () => {
      if (isRecoveryMode) setIsRecoveryMode(false);
      setLoading(true);
      try {
        localStorage.removeItem('is_guest');
        // 🆕 مسح بيانات offline قبل الخروج
        if (user?.id && user.id !== 'guest') {
          await offlineStorage.clearAllUserData(user.id);
          localStorage.removeItem(`user_type_${user.id}`);
        }
        if (user?.id !== 'guest') {
          await supabaseService.signOut();
        }
        setUser(null);
        setUserType(null);
        setError(null);
        retryCountRef.current = 0;
        setIsRecoveryMode(false);
        if (isBrowser) {
          localStorage.removeItem('supabase.auth.token');
          sessionStorage.clear();
        }
      } catch (error) {
        console.error('Sign out error:', error);
        setError("فشل تسجيل الخروج");
        throw error;
      } finally {
        setLoading(false);
      }
    },

    loginAsGuest: () => {
      localStorage.setItem('is_guest', 'true');
      setUser({ id: 'guest', email: 'guest@khidma.com', isGuest: true });
      setUserType('client');
    },

    refreshUserType: async () => {
      if (!user || isRecoveryMode) return null;
      try {
        const type = await checkUserType(user.id);
        setUserType(type);
        retryCountRef.current = 0;
        return type;
      } catch (error) {
        console.error('Refresh user type error:', error);
        return null;
      }
    },
    
    reloadAuth,
    isAuthenticated: !!user && !isRecoveryMode,
    isProvider: userType === 'provider' && !isRecoveryMode,
    isClient:   userType === 'client'   && !isRecoveryMode,
    getUserId:  () => user?.id || null,
    clearError: () => setError(null),
    exitRecoveryMode: () => setIsRecoveryMode(false),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}

export function withAuth(Component) {
  return function WithAuthWrapper(props) {
    const auth = useAuth();
    return <Component {...props} auth={auth} />;
  };
}